import {GetOrderSuccessPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/service.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useIsRoktConsetGiven} from '@aboutyou/storage';
import {
  COLORS,
  ContentPopperProps,
  ErrorBoundary,
  SPACING,
  desktopAndUpLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {useSessionId} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {useLiveShoppingPurchaseTracking} from '../../../client-services/LiveShoppingPurchaseTracking/useLiveShoppingPurchaseTracking';
import {Advertisement} from '../../../features/Advertisement/Advertisement';
import {useIsMissionsActive} from '../../../features/Coins/utils/useIsMissionsActive';
import {PromotionBanner} from '../../../features/PromotionBanner/PromotionBanner';
import {useIsNewBannerFrameworkActive} from '../../../features/PromotionBanner/utils/useIsNewBannerFrameworkActive';
import {useInvalidateWallet} from '../../Wallet/utils/useInvalidateGetWallet';
import {BankDataForm} from '../BankDataForm/BankDataForm';
import {ErrorSection} from '../ErrorSection/ErrorSection';
import {FollowBrands} from '../FollowBrands/FollowBrands';
import {NewsletterSection} from '../NewsletterFormSection/NewsletterSection';
import {NextActionContactSection} from '../NextActionContactSection/NextActionContactSection';
import {OrderDetails} from '../OrderDetails/OrderDetails';
import {OrderSummary} from '../OrderSummary/OrderSummary';
import {RoktLauncherConditional} from '../Rokt/RoktContextProvider';
import {RoktLauncher} from '../Rokt/RoktLauncher';
import {SovendusBanner} from '../Sovendus/SovendusBanner';
import {TrustedShop} from '../TrustedShop/TrustedShop';
import {OSPErrorView} from './OspErrorView';

export const OrderSuccessPageStreamView = ({
  items,
  className,
  ContentPopperLayoutComponent,
}: {
  items: GetOrderSuccessPageResponse[];
  cbdParameter: string;
  className?: string;
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
}) => {
  const invalidateGetWallet = useInvalidateWallet();
  const isConsentAccepted = useIsRoktConsetGiven();
  useLiveShoppingPurchaseTracking(items);
  const {data} = useSessionId();
  const isMissionsActive = useIsMissionsActive();
  const activateNewBannerFramework = useIsNewBannerFrameworkActive();

  const newsletterSection = items.find(
    item => item.section.type?.$case === 'newsletter',
  );

  useEffect(() => {
    // Invalidate the GetWallet request after the OSP is loaded to get the latest mission progress.
    if (isMissionsActive) {
      invalidateGetWallet();
    }
  }, [invalidateGetWallet, isMissionsActive]);

  return (
    <Container className={className}>
      {items.map(item => {
        switch (item.section.type?.$case) {
          case 'error':
            return <ErrorSection key="error" error={item.section.type.error} />;
          case 'orderSummary':
            return (
              <Row>
                <ErrorBoundary
                  key="osp_orderSummary"
                  ErrorComponent={OSPErrorView}
                  renderContext="osp_orderSummary"
                >
                  <StyledOrderSummary
                    subheadline={item.section.type.orderSummary.subtitle}
                    headline={item.section.type.orderSummary.headline}
                    infos={item.section.type.orderSummary.infos}
                    transactionTracker={
                      item.section.type.orderSummary.transactionTracker
                    }
                    isResponsive={!newsletterSection}
                  />
                </ErrorBoundary>
                {newsletterSection && (
                  <ErrorBoundary
                    key="osp_newsletter"
                    ErrorComponent={null}
                    renderContext="osp_newsletter"
                  >
                    <StyledNewsletterSection
                      {...(newsletterSection.section.type!.$case ===
                        'newsletter' &&
                        newsletterSection.section.type.newsletter)}
                      NewsletterLayoutComponent={ContentPopperLayoutComponent}
                    />
                  </ErrorBoundary>
                )}
              </Row>
            );
          case 'missingBankData':
            return (
              <ErrorBoundary
                key="osp_missingBankData"
                ErrorComponent={null}
                renderContext="osp_missingBankData"
              >
                <BankDataForm {...item.section.type.missingBankData} />
              </ErrorBoundary>
            );
          case 'roktPlacement': {
            const roktPlacement = item.section.type.roktPlacement;
            if (!isConsentAccepted) return null;
            return (
              <ErrorBoundary
                key="osp_roktPlacement"
                ErrorComponent={null}
                renderContext="osp_roktPlacement"
              >
                <RoktStreamWrapper>
                  <RoktLauncherConditional>
                    <RoktLauncher
                      amount={roktPlacement.amount}
                      confirmationref={roktPlacement.orderId.toString()}
                      country={roktPlacement.countryCode}
                      currency={roktPlacement.money.currencyCode}
                      dob={roktPlacement.dateOfBirth}
                      emailsha256={roktPlacement.emailSha256}
                      firstname={roktPlacement.firstName}
                      lastname={roktPlacement.lastName}
                      language={roktPlacement.language}
                      postcode={roktPlacement.postcode}
                    />
                  </RoktLauncherConditional>
                </RoktStreamWrapper>
              </ErrorBoundary>
            );
          }
          case 'sovendusPlacement': {
            const sovendusPlacement = item.section.type.sovendusPlacement;
            return (
              <ErrorBoundary
                key="osp_sovendusPlacement"
                ErrorComponent={null}
                renderContext="osp_sovendusPlacement"
              >
                <SovendusStreamWrapper>
                  <SovendusBanner
                    orderId={sovendusPlacement.orderId.toString()}
                    orderValue={sovendusPlacement.money.amount}
                    orderCurrency={sovendusPlacement.money.currencyCode}
                    sessionId={data!.sessionId}
                    firstName={sovendusPlacement.firstName}
                    lastName={sovendusPlacement.lastName}
                    countryCode={sovendusPlacement.countryCode}
                    salutation={sovendusPlacement.salutation}
                    trafficMediumNumber={sovendusPlacement.trafficMediumNumber}
                    trafficSourceNumber={sovendusPlacement.trafficSourceNumber}
                    zipCode={sovendusPlacement.postcode}
                    email={sovendusPlacement.email}
                    cityName={sovendusPlacement.city}
                    streetName={sovendusPlacement.street}
                    streetNumber={sovendusPlacement.houseNumber}
                  />
                </SovendusStreamWrapper>
              </ErrorBoundary>
            );
          }
          case 'basBanner':
            return (
              <ErrorBoundary
                key="osp_basBanner"
                ErrorComponent={null}
                renderContext="osp_basBanner"
              >
                {activateNewBannerFramework ? (
                  <StreamAdvertisement $visible>
                    <PromotionBanner
                      pageType="orderSuccess"
                      ContentPopperLayoutComponent={
                        ContentPopperLayoutComponent
                      }
                    />
                  </StreamAdvertisement>
                ) : (
                  <StreamAdvertisement
                    $visible={!!item.section.type.basBanner.advertisement}
                  >
                    <StyledAdvertisement
                      advertisement={item.section.type.basBanner.advertisement}
                      testId="advertisementOsp"
                      tracking={{
                        promotionSource: TrackingPromotionCreative.BasOspBanner,
                      }}
                    />
                  </StreamAdvertisement>
                )}
              </ErrorBoundary>
            );
          case 'followBrands':
            return (
              <ErrorBoundary
                key="osp_followBrands"
                ErrorComponent={null}
                renderContext="osp_followBrands"
              >
                <FollowBrands {...item.section.type.followBrands} />
              </ErrorBoundary>
            );
          case 'orderDetails':
            return (
              <ErrorBoundary
                key="osp_orderDetails"
                ErrorComponent={null}
                renderContext="osp_orderDetails"
              >
                <OrderDetails {...item.section.type.orderDetails} />
              </ErrorBoundary>
            );
          case 'nextActions':
            return (
              <ErrorBoundary
                key="osp_nextActions"
                ErrorComponent={null}
                renderContext="osp_nextActions"
              >
                <NextActionContactSection {...item.section.type.nextActions} />
              </ErrorBoundary>
            );
          case 'trustedShops':
            return (
              <ErrorBoundary
                key="osp_trustedShops"
                ErrorComponent={null}
                renderContext="osp_trustedShops"
              >
                <TrustedShop {...item.section.type.trustedShops} />
              </ErrorBoundary>
            );
          default:
            return null;
        }
      })}
    </Container>
  );
};

export const RoktStreamWrapper = styled.div`
  width: 100%;

  background-color: ${COLORS.White};
  padding: 0 1rem;

  ${tabletAndUpLinaria(`
    padding: 0;
  `)}
`;

export const SovendusStreamWrapper = styled.div`
  width: 100%;

  background-color: ${COLORS.White};
  padding: 0 1rem;

  ${tabletAndUpLinaria(`
    padding: 0;
  `)}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StreamAdvertisement = styled.div<{$visible: boolean}>`
  --padding: 2rem 1rem 1rem;

  ${tabletAndUpLinaria(`
    --padding: 2rem 0rem 1rem;
  `)}

  padding: ${props => (props.$visible ? 'var(--padding)' : 'unset')};
  background-color: ${COLORS.White};

  /* When the (new framework) banner resolves to no banner it renders nothing,
     so collapse the padding to avoid an empty white spacer on the OSP. */
  &:empty {
    padding: unset;
  }
`;

const StyledAdvertisement = styled(Advertisement)`
  &&& {
    border-radius: 0.25rem;
    overflow: hidden;
    aspect-ratio: 2/1;

    ${tabletAndUpLinaria(`
      border-radius: unset;
      aspect-ratio: 4/1;
    `)}
    background-color: ${COLORS.White};
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;

  ${tabletAndUpLinaria(`
    flex-direction: row;
    align-items: stretch;
    gap: ${SPACING['X.Xl']}
  `)}

  ${desktopAndUpLinaria(`
    gap: ${SPACING['X.Xl5']}
  `)}
`;

const StyledOrderSummary = styled(OrderSummary)`
  flex: 1;
`;

const StyledNewsletterSection = styled(NewsletterSection)`
  width: 100%;

  ${tabletAndUpLinaria(`
    width: 390px;
  `)}
`;
